import { useEffect, useState } from "react"
import type { BudgetTreeLaunch } from "@models/engagementBudgetTree.model"
import { numericOnly } from "./LaunchNetworksTable"

const round2 = (n: number) => Math.round(n * 100) / 100
const roundPct = (n: number) => Math.round(n * 1e6) / 1e6
const fmt = (v: number) => v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export function EngagementLaunchesTable({
  launches,
  funding,
  editing,
  onSetPercent,
  onSelectLaunch,
  onOverAllocatedChange,
}: {
  launches: BudgetTreeLaunch[]
  /** The engagement's remaining flight funding (MPN-1789), not its cash balance. */
  funding: number
  editing: boolean
  onSetPercent: (launchId: number, percent: number) => void
  onSelectLaunch: (launchId: number) => void
  /** Raised whenever the campaigns' total crosses 100%, so the host can block Done. */
  onOverAllocatedChange?: (over: boolean) => void
}) {
  const totalPct = launches.reduce((s, l) => s + l.percent, 0)
  // Pre-margin gross: each launch's share of the engagement's remaining flight funding.
  const totalGross = launches.reduce((s, l) => s + (funding * l.percent) / 100, 0)
  const overTotal = totalPct > 100 + 0.01
  const unallocated = round2(100 - totalPct)
  const unallocatedAmount = round2(funding * (unallocated / 100))
  // Named separately from `overTotal`, which only tints the Total cell: this one decides
  // whether Save is allowed through (MPN-1804).
  const over = unallocated < -0.01

  // Reported up rather than read down: the table already computes the total from the live
  // drafts, and recomputing it in the host would be a second source of the same truth.
  useEffect(() => {
    onOverAllocatedChange?.(over)
  }, [over, onOverAllocatedChange])

  return (
    <table className="budget-table">
      <thead className="budget-table__head">
        <tr>
          <th className="col-name">Campaign</th>
          <th className="col-pct">
            Percent <span className="budget-col-unit">(%)</span>
          </th>
          <th className="col-resolved">
            Amount <span className="budget-col-unit">($)</span>
          </th>
          <th className="col-go" />
        </tr>
      </thead>
      <tbody>
        {launches.map((l) => {
          const sibling = launches.reduce(
            (s, x) => s + (x.launch_id === l.launch_id ? 0 : x.percent),
            0,
          )
          return (
            <LaunchCoupledRow
              key={l.launch_id}
              launch={l}
              funding={funding}
              editing={editing}
              siblingSumExcludingSelf={sibling}
              onSelectLaunch={() => onSelectLaunch(l.launch_id)}
              onCommitPercent={(p) => onSetPercent(l.launch_id, p)}
            />
          )
        })}
      </tbody>
      <tfoot>
        <tr className="budget-table__total-row">
          <td className="col-name" style={{ fontWeight: 600 }}>Total</td>
          <td className="col-pct">
            <ReadonlyShell suffix="%" valueText={totalPct.toFixed(2)} bold over={overTotal} />
          </td>
          <td className="col-resolved">
            <ReadonlyShell valueText={`${fmt(totalGross)} / ${fmt(funding)}`} bold />
          </td>
          <td className="col-go" />
        </tr>
        {Math.abs(unallocated) > 0.01 && (
          <tr className="budget-table__unallocated-row">
            <td className="col-name">
              <span className={over ? "budget-overallocated" : "budget-unallocated"}>
                {over ? (
                  <>
                    <strong>!&nbsp;</strong>Over-allocated — reduce by{" "}
                    {Math.abs(unallocated).toFixed(2)}% ({fmt(Math.abs(unallocatedAmount))})
                  </>
                ) : (
                  "Unallocated"
                )}
              </span>
            </td>
            <td className="col-pct">
              <ReadonlyShell suffix="%" valueText={Math.abs(unallocated).toFixed(2)} muted />
            </td>
            <td className="col-resolved">
              <ReadonlyShell valueText={fmt(Math.abs(unallocatedAmount))} muted />
            </td>
            <td className="col-go" />
          </tr>
        )}
      </tfoot>
    </table>
  )
}

function LaunchCoupledRow({
  launch,
  funding,
  editing,
  siblingSumExcludingSelf,
  onSelectLaunch,
  onCommitPercent,
}: {
  launch: BudgetTreeLaunch
  funding: number
  editing: boolean
  siblingSumExcludingSelf: number
  onSelectLaunch: () => void
  onCommitPercent: (p: number) => void
}) {
  const [pctDraft, setPctDraft] = useState(launch.percent)
  useEffect(() => setPctDraft(launch.percent), [launch.percent])

  const liveResolved = (funding * pctDraft) / 100
  const projected = pctDraft + siblingSumExcludingSelf
  const overTotal = projected > 100 + 0.01
  const outOfRange = pctDraft < 0 || pctDraft > 100
  const error = (overTotal || outOfRange) && editing

  const commit = () => {
    if (outOfRange) {
      setPctDraft(launch.percent)
      return
    }
    if (roundPct(pctDraft) !== roundPct(launch.percent)) onCommitPercent(roundPct(pctDraft))
  }

  const cancel = () => setPctDraft(launch.percent)

  return (
    /*
        The whole row opens the campaign. The two editable cells swallow the click only
        while Edit is on, so typing a percent in place doesn't navigate away from it --
        outside Edit they are ordinary row, because a dead strip down the middle of a
        clickable row leaves only the name and the chevron working.
    */
    <tr
      className={`budget-table__row budget-table__row--link${error ? " budget-table__row--warning" : ""}`}
      onClick={onSelectLaunch}
    >
      <td className="col-name">
        <span className="budget-name-cell">{launch.launch_name}</span>
        {/* A campaign on spend windows reacts differently to a share change than a flat
            one does — its windows rescale — so anyone editing this column needs to see
            which rows those are (MPN-1804). */}
        {launch.spend_pattern && launch.spend_pattern !== "LEGACY" && (
          <span
            className="budget-segmented-tag"
            title="On spend windows. Changing this share rescales its upcoming windows; elapsed days keep their dollars."
          >
            Segmented
          </span>
        )}
      </td>
      <td className="col-pct" onClick={(e) => editing && e.stopPropagation()}>
        <SyncedInput
          value={pctDraft}
          formattedDisplay={pctDraft.toFixed(2)}
          editing={editing}
          error={error}
          onChangeNum={(v) => setPctDraft(v)}
          onCommit={commit}
          onCancel={cancel}
        />
      </td>
      <td className="col-resolved" onClick={(e) => editing && e.stopPropagation()}>
        <SyncedInput
          value={liveResolved}
          formattedDisplay={fmt(liveResolved)}
          editing={editing && funding > 0}
          error={error}
          onChangeNum={(v) => funding > 0 && setPctDraft((v / funding) * 100)}
          onCommit={commit}
          onCancel={cancel}
        />
      </td>
      <td className="col-go">
        <span className="budget-table__chev">›</span>
      </td>
    </tr>
  )
}

function SyncedInput({
  value,
  formattedDisplay,
  editing,
  error,
  prefix,
  suffix,
  onChangeNum,
  onCommit,
  onCancel,
}: {
  value: number
  formattedDisplay: string
  editing: boolean
  error: boolean
  prefix?: string
  suffix?: string
  onChangeNum: (v: number) => void
  onCommit: () => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const cls =
    "budget-inline-input" +
    (!editing ? " budget-inline-input--readonly" : "") +
    (error && editing ? " budget-inline-input--error" : "")

  const display = draft !== null ? draft : formattedDisplay

  return (
    <div className={cls}>
      {prefix && <span className="budget-inline-input__prefix">{prefix}</span>}
      <input
        type="text"
        inputMode="decimal"
        className="budget-inline-input__field"
        readOnly={!editing}
        tabIndex={editing ? 0 : -1}
        value={display}
        onChange={(e) => {
          const next = numericOnly(e.target.value)
          setDraft(next)
          const v = parseFloat(next.replace(/,/g, ""))
          if (!isNaN(v)) onChangeNum(v)
        }}
        onFocus={() => editing && setDraft(value.toFixed(2))}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setDraft(null)
            onCancel()
            ;(e.target as HTMLInputElement).blur()
          } else if (e.key === "Enter") {
            (e.target as HTMLInputElement).blur()
          }
        }}
        onBlur={() => {
          setDraft(null)
          onCommit()
        }}
      />
      {suffix && <span className="budget-inline-input__prefix">{suffix}</span>}
    </div>
  )
}

function ReadonlyShell({
  prefix,
  suffix,
  valueText,
  bold = false,
  over = false,
  muted = false,
}: {
  prefix?: string
  suffix?: string
  valueText: string
  bold?: boolean
  over?: boolean
  muted?: boolean
}) {
  return (
    <div className="budget-inline-input budget-inline-input--readonly">
      {prefix && <span className="budget-inline-input__prefix">{prefix}</span>}
      <input
        type="text"
        readOnly
        tabIndex={-1}
        className="budget-inline-input__field"
        style={{
          fontWeight: bold ? 600 : undefined,
          color: over ? "#ef4444" : muted ? "#94a3b8" : undefined,
        }}
        value={valueText}
      />
      {suffix && <span className="budget-inline-input__prefix">{suffix}</span>}
    </div>
  )
}
